import { useState, useEffect } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { useNavigate } from "react-router-dom";
import { STATIC_BASE_URL } from "../../config";
import axios from "../../api/axiosInstance";

interface Profile {
    id: number;
    name: string;
    email: string;
    phone_number: string;
    country: string;
    address: string;
    website: string;
    business_hours: string;
    cuisine_type: string;
    description: string;
    facebook_page: string;
    instagram_handle: string;
    whatsapp_number: string;
    is_delivery_available: boolean;
    is_takeaway_available: boolean;
    logo_url: string;
    slug: string;
    profile_picture?: string;
}

interface UpdateProfileResponse {
    message: string;
}

export default function ProfileSettings() {
    const [profile, setProfile] = useState<Profile>({
        id: 0,
        name: "",
        email: "",
        phone_number: "",
        country: "",
        address: "",
        website: "",
        business_hours: "",
        cuisine_type: "",
        description: "",
        facebook_page: "",
        instagram_handle: "",
        whatsapp_number: "",
        is_delivery_available: false,
        is_takeaway_available: false,
        logo_url: "",
        slug: "",
    });

    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [previewLogo, setPreviewLogo] = useState<string | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!profile.name.trim()) {
            newErrors.name = "Restaurant name is required.";
        }

        if (!profile.phone_number.trim()) {
            newErrors.phone_number = "Phone number is required.";
        } else if (!/^\+?[0-9\s\-]{7,15}$/.test(profile.phone_number)) {
            newErrors.phone_number = "Invalid phone number format.";
        }

        if (!profile.country.trim()) {
            newErrors.country = "Country is required.";
        }

        if (!profile.address.trim()) {
            newErrors.address = "Address is required.";
        }

        if (!previewLogo && !logoFile) {
            newErrors.logo = "Profile picture is required.";
        }

        if (profile.website.trim() && !/^https?:\/\/.+\..+/.test(profile.website)) {
            newErrors.website = "Website must start with http:// or https://";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // useEffect(() => {
    //     const token = localStorage.getItem("access_token");
    //     if (!token) {
    //         navigate("/login");
    //         return;
    //     }
    //
    //     axios
    //         .get<Profile>("/api/profile")
    //         .then((res) => {
    //             setProfile(res.data);
    //             if (res.data.logo_url) {
    //                 setPreviewLogo(`${STATIC_BASE_URL}/profile_pictures/${res.data.logo_url}`);
    //             }
    //         })
    //         .catch((err) => {
    //             console.error("Error fetching profile:", err);
    //         });
    // }, []);
    useEffect(() => {
        const token = localStorage.getItem("access_token");
        if (!token) {
            navigate("/login");
            return;
        }

        axios
            .get<Profile>("/api/profile")
            .then((res) => {
                setProfile(res.data);
                if (res.data.slug) {
                    setPreviewLogo(`${STATIC_BASE_URL}/restaurants/${res.data.slug}/${res.data.logo_url}`);
                }
            })
            .catch((err) => {
                console.error("Error fetching profile:", err);
            });
    }, []);


    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setProfile((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleCountryChange = (selectedOption: any) => {
        setProfile((prev) => ({ ...prev, country: selectedOption.value }));
        setErrors((prev) => ({ ...prev, country: "" }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setProfile((prev) => ({ ...prev, [name]: checked }));
    };

    const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            const file = e.target.files[0];
            setLogoFile(file);
            setPreviewLogo(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        const formData = new FormData();
        Object.entries(profile).forEach(([key, value]) => {
            formData.append(key, String(value));
        });

        if (logoFile) formData.append("logo", logoFile);

        try {
            const response = await axios.post<UpdateProfileResponse>("/api/profile/update", formData);
            alert(response.data.message);
        } catch (error: any) {
            console.error("Update Error:", error);
            alert(error.response?.data?.error || "Update failed");
        }
    };

    return (
        <>
            <div className="max-w-5xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">⚙️ Profile Settings</h2>

                <form onSubmit={handleSubmit} className="bg-white shadow-md rounded-lg p-6 md:p-10 space-y-8">

                    {/* Logo */}
                    <div className="flex justify-center">
                        <label className="relative cursor-pointer">
                            <img
                                src={previewLogo || "/default-logo.png"}
                                alt="Logo"
                                className="w-28 h-28 md:w-36 md:h-36 rounded-full border object-cover shadow-md"
                            />
                            <input type="file" className="absolute inset-0 opacity-0 w-full h-full"
                                   onChange={handleLogoChange}/>
                        </label>
                    </div>
                    <p className="text-center text-sm text-gray-500 -mt-4">Tap logo to upload</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name */}
                        <div>
                            <label className="block text-gray-700 font-medium mb-1">Restaurant Name</label>
                            <input
                                type="text"
                                name="name"
                                value={profile.name}
                                onChange={handleChange}
                                className="w-full border rounded-lg p-3"
                                required
                            />
                            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                        </div>

                        {/* Email */}
                        <div>
                            <label className="block text-gray-700 font-medium mb-1">Email</label>
                            <input
                                type="email"
                                name="email"
                                value={profile.email}
                                className="w-full border rounded-lg p-3 bg-gray-100 cursor-not-allowed"
                                disabled
                            />
                        </div>

                        {/* Phone */}
                        <div>
                            <label className="block text-gray-700 font-medium mb-1">Phone Number</label>
                            <input
                                type="text"
                                name="phone_number"
                                value={profile.phone_number}
                                onChange={handleChange}
                                className="w-full border rounded-lg p-3"
                                required
                            />
                            {errors.phone_number && <p className="text-red-500 text-sm mt-1">{errors.phone_number}</p>}
                        </div>

                        {/* Address */}
                        <div>
                            <label className="block text-gray-700 font-medium mb-1">Address</label>
                            <input
                                type="text"
                                name="address"
                                value={profile.address}
                                onChange={handleChange}
                                className="w-full border rounded-lg p-3"
                                required
                            />
                            {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                        </div>

                        {/* Country */}
                        <div className="col-span-1 md:col-span-2">
                            <label className="block text-gray-700 font-medium mb-1">Country</label>
                            <Select
                                options={countryList().getData()}
                                value={countryList().getData().find((c) => c.value === profile.country)}
                                onChange={handleCountryChange}
                                className="w-full"
                                required
                            />
                            {errors.country && <p className="text-red-500 text-sm mt-1">{errors.country}</p>}
                        </div>

                        {/* Business */}
                        <input
                            name="business_hours"
                            placeholder="Business Hours"
                            value={profile.business_hours}
                            onChange={handleChange}
                            className="w-full border rounded-lg p-3"
                        />
                        <input
                            name="cuisine_type"
                            placeholder="Cuisine Type"
                            value={profile.cuisine_type}
                            onChange={handleChange}
                            className="w-full border rounded-lg p-3"
                            required
                        />

                        {/* Socials */}
                        <input
                            name="facebook_page"
                            placeholder="Facebook Page"
                            value={profile.facebook_page}
                            onChange={handleChange}
                            className="w-full border rounded-lg p-3"
                        />
                        <input
                            name="instagram_handle"
                            placeholder="Instagram Handle"
                            value={profile.instagram_handle}
                            onChange={handleChange}
                            className="w-full border rounded-lg p-3"
                        />
                        <input
                            name="whatsapp_number"
                            placeholder="WhatsApp Number"
                            value={profile.whatsapp_number}
                            onChange={handleChange}
                            className="w-full border rounded-lg p-3"
                        />

                        {/* Website */}
                        <div className="col-span-1 md:col-span-2">
                            <input
                                name="website"
                                placeholder="Website URL"
                                value={profile.website}
                                onChange={handleChange}
                                className="w-full border rounded-lg p-3"
                                required
                            />
                        </div>

                        {/* Description */}
                        <div className="col-span-1 md:col-span-2">
                            <textarea
                                name="description"
                                placeholder="Short Description"
                                value={profile.description}
                                onChange={handleChange}
                                className="w-full border rounded-lg p-3 min-h-[100px]"
                                required
                            />
                        </div>

                        {/* Toggles */}
                        <div
                            className="col-span-1 md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="is_delivery_available"
                                    checked={profile.is_delivery_available}
                                    onChange={handleCheckboxChange}
                                />
                                <span>🚗 Delivery Available</span>
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="is_takeaway_available"
                                    checked={profile.is_takeaway_available}
                                    onChange={handleCheckboxChange}
                                />
                                <span>🥡 Takeaway Available</span>
                            </label>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="text-center mt-8">
                        <button
                            type="submit"
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-lg shadow-md transition"
                        >
                            💾 Save Changes
                        </button>
                    </div>
                </form>
            </div>

        </>
    );
}
